<script setup lang="ts">
import {
  AbLandingPageTestDocument,
  IsAbTestPageVisibleForLoggedInUsersDocument,
  IsPageVisibleForLoggedInUsersDocument,
  LandingPageDocument,
} from '~/graphql/generated'

import Box from '~/components/dato/Box.vue'
import Hero from '~/components/dato/Hero.vue'
import Navbar from '~/components/dato/Navbar/Navbar.vue'

import { useDatoEvaluateBoxComponents } from '~/composables/useDatoEvaluateBoxComponents'
import { useDatoPageHeadData } from '~/composables/useDatoPageHeadData'
import { useGraphqlQuery } from '~/composables/useGraphqlQuery'
import { useGrowthBookSetup } from '~/composables/useGrowthBookSetup'

import { useBandSignupStore } from '~/stores/bandSignup'
import { useLoginStore } from '~/stores/login'
import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserStore } from '~/stores/user'

import {
  AB_TEST_BUCKET_EVENT_STORAGE_KEY,
  getABTestBucket,
} from '~/helpers/LandingPageABTest'

import type { CommonContentRecord } from '~/graphql/generated'

const route = useRoute()
const { locale } = useI18n()

const { queryDatoCms } = useGraphqlQuery()

const { IS_LOGGED_IN, IS_INCOMPLETE_USER } = storeToRefs(useUserStore())
const { isTransitioningToSignup: IS_MID_PAGE_TRANSITION } = storeToRefs(
  useMiscBandSignupStore(),
)
const { display: LOGIN_IS_DISPLAYED } = storeToRefs(useLoginStore())
const { display: BAND_SIGNUP_IS_DISPLAYED } = storeToRefs(useBandSignupStore())
const { SET_DISPLAY: LOGIN_SET_DISPLAY } = useLoginStore()
const { SETUP_EVENT_LISTENERS: SETUP_RESIZE_EVENT_LISTENERS } =
  useMiscResizeStore()

const { getGrowthBookDeviceId } = useGrowthBookSetup()

const shouldDisplayIncompleteSignupBanner = computed(() => {
  if (route.path.includes('signup/')) return false
  return IS_INCOMPLETE_USER.value && !IS_MID_PAGE_TRANSITION.value
})

definePageMeta({
  async middleware(to) {
    const { $localePath, $pinia } = useNuxtApp()
    const { IS_LOGGED_IN } = useUserStore($pinia)
    const { queryDatoCms } = useGraphqlQuery()
    const { getGrowthBookDeviceId } = useGrowthBookSetup()

    // first check if the page is an AB test page
    const abTestPageResponse = await queryDatoCms(
      IsAbTestPageVisibleForLoggedInUsersDocument,
      {
        slug: to.params.slug,
      },
    ).catch(() => null)

    if (
      abTestPageResponse &&
      abTestPageResponse.data?.value?.abLandingPageTest
    ) {
      const { abLandingPageTest } = abTestPageResponse.data.value
      const deviceId = getGrowthBookDeviceId()
      const bucket = getABTestBucket(
        abLandingPageTest.variantASplit || 50,
        abLandingPageTest.variantBSplit || 50,
        deviceId,
      )
      const isAvailableForLoggedInUsers =
        bucket === 0
          ? abLandingPageTest.variantAPage?.isPageVisibleForLoggedInUsers
          : abLandingPageTest.variantBPage?.isPageVisibleForLoggedInUsers
      if (isAvailableForLoggedInUsers || !IS_LOGGED_IN) return
      return navigateTo($localePath('/'))
    }

    // otherwise use the normal landing page query
    const response = await queryDatoCms(IsPageVisibleForLoggedInUsersDocument, {
      slug: to.params.slug,
    }).catch(console.error)

    if (!response?.data.value?.landingPage)
      throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })

    const isAvailableForLoggedInUsers =
      response.data.value.landingPage.isPageVisibleForLoggedInUsers

    if (isAvailableForLoggedInUsers || !IS_LOGGED_IN) return
    return navigateTo($localePath('/'))
  },
  layout: false,
})

const head = useLocaleHead({
  dir: true,
  key: 'id',
  seo: true,
})

const { data } = await useAsyncData(
  `lp-${route.params.slug ?? 'main-landing-page'}`,
  async () => {
    const MAIN_LANDING_PAGE_SLUG = 'main-lp-groover-co'
    // check if there's an AB Test page that matches the slug
    // if not, get the regular landing page response
    const abTestResponse = await queryDatoCms(AbLandingPageTestDocument, {
      slug: route.params.slug || MAIN_LANDING_PAGE_SLUG,
      locale: locale.value,
    }).catch(() => null)

    if (abTestResponse && abTestResponse.data?.value?.abLandingPageTest) {
      const { abLandingPageTest } = abTestResponse.data.value
      const deviceId = getGrowthBookDeviceId()
      const bucket = getABTestBucket(
        abLandingPageTest.variantASplit || 50,
        abLandingPageTest.variantBSplit || 50,
        deviceId,
      )

      return {
        landingPage:
          bucket === 0
            ? abLandingPageTest.variantAPage
            : abLandingPageTest.variantBPage,
        commonContent: abTestResponse.data?.value.commonContent,
        pageVariant: `${bucket === 0 ? 'A' : 'B'}`,
      }
    }

    const response = await queryDatoCms(LandingPageDocument, {
      slug: route.params.slug || MAIN_LANDING_PAGE_SLUG,
      locale: locale.value,
    }).catch(() => null)

    return {
      landingPage: response?.data.value?.landingPage ?? null,
      commonContent: response?.data.value?.commonContent ?? null,
      pageVariant: null,
    }
  },
)

if (data.value === null || !data.value.landingPage)
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })

onMounted(() => {
  trackAndStorePageVariantView()
  SETUP_RESIZE_EVENT_LISTENERS()
  checkForNextQuery()
})

const { landingPage, commonContent } = data.value
const { getDatoPageHeadData } = useDatoPageHeadData()
useHead(() =>
  getDatoPageHeadData({
    seoData: landingPage.seo,
    seoCanonicalURLSlug: landingPage.seoCanonicalUrlSlug,
    isMainLandingPage: !route.params.slug,
  }),
)

const { boxRecordSections } = useDatoEvaluateBoxComponents(
  () => landingPage.sections,
)

/**
 * When there is a next query open the login and it will automaticaly redirct to the next url.
 */
function checkForNextQuery() {
  const {
    query: { next },
  } = route

  if (!next || IS_LOGGED_IN.value) return
  LOGIN_SET_DISPLAY(true)
}

/**
 * Track the page variant view for AB testing using GTM and localStorage.
 */
function trackAndStorePageVariantView() {
  if (!data || !data.value?.pageVariant) return

  // push the variant to the dataLayer for gtm tracking purposes (since we don't use segment for non-logged-in tracking)
  // @ts-expect-error global variable
  window.dataLayer = window.dataLayer || []
  // @ts-expect-error global variable
  if (window.dataLayer) {
    // @ts-expect-error global variable
    window.dataLayer.push({
      event: AB_TEST_BUCKET_EVENT_STORAGE_KEY,
      pageSlug: route.params.slug,
      variant: data.value.pageVariant,
    })
  }

  // store the variant in localStorage so we can use the variant in other tracking calls
  window.localStorage.setItem(
    AB_TEST_BUCKET_EVENT_STORAGE_KEY,
    JSON.stringify({
      slug: route.params.slug,
      variant: data.value.pageVariant,
    }),
  )
}
</script>

<template>
  <Html :lang="head.htmlAttrs?.lang" :dir="head.htmlAttrs?.dir">
    <div v-if="landingPage">
      <Navbar
        v-if="landingPage.navbar?.navbar?.navbar"
        v-bind="landingPage.navbar?.navbar?.navbar"
        :landing-page-available-locales="[...landingPage.slugLocales]"
      />
      <LazyNavWarningsIncompleteSignupBanner
        v-if="shouldDisplayIncompleteSignupBanner"
        :key="route.fullPath"
      />
      <div
        v-if="landingPage.hero"
        class="tw-bg-fill tw-pb-2xl tw-pt-4xl md:tw-pb-3xl md:tw-pt-5xl lg:tw-pb-4xl lg:tw-pt-7xl"
      >
        <Hero v-bind="landingPage.hero" />
      </div>
      <div
        v-for="(box, index) in boxRecordSections"
        :id="`box-${index + 1}`"
        :key="box.id"
      >
        <Box
          :key="`${box.id}-${index}`"
          v-bind="{
            ...box,
            commonContent: commonContent as CommonContentRecord,
          }"
        />
      </div>
      <LazyVisibleSharedFooter hydrate-on-visible />
      <Transition name="fade" mode="out-in">
        <LazyTimeIntercomButton :hydrate-after="5000" />
      </Transition>
      <Transition name="fade" mode="out-in">
        <LazyAuthLogin
          v-if="LOGIN_IS_DISPLAYED"
          :model-value="LOGIN_IS_DISPLAYED"
          class="inLayoutIgnoreFontUpscale ignoreFontUpscale"
          @update:model-value="LOGIN_SET_DISPLAY"
        />
      </Transition>
      <Transition name="fade" mode="out-in">
        <LazyBandSignupInit
          v-if="BAND_SIGNUP_IS_DISPLAYED"
          class="inLayoutIgnoreFontUpscale ignoreFontUpscale"
        />
      </Transition>
    </div>
  </Html>
</template>

<style scoped lang="scss">
:deep(.mainParentContainer) {
  @apply tw-max-w-[1200px] tw-px-lg md:tw-px-xl min-[1280px]:tw-mx-auto min-[1280px]:tw-px-0;
}
</style>
